import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Avatar, Table, TableHead, TableBody,
  TableRow, TableCell, TableSortLabel, TablePagination, TextField, InputAdornment,
  IconButton, Tooltip, Skeleton, Button,
} from '@mui/material';
import {
  Search as SearchIcon, Clear as ClearIcon,
  ArrowForwardIos, Person as PersonIcon, ChildCare,
  WarningAmber, Receipt as ReceiptIcon,
  SwapHoriz,
} from '@mui/icons-material';
import axios from 'axios';
import { billSummaryBulkdb } from '../BaseURL';
import { formatAge } from '../Message';

// Currency formatter for Ghanaian Cedi
const fmtGHS = (v) =>
  new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS', minimumFractionDigits: 2 })
    .format(Number(v) || 0);

// Color coding for outstanding balance amounts
const balanceColor = (amount) => {
  if (!amount || amount <= 0) return '#15803D';   // green — paid up
  if (amount < 200)            return '#475569';   // neutral
  if (amount < 1000)           return '#d97706';   // amber
  return '#dc2626';                                // red — large outstanding
};

// Format a vitals timestamp as e.g. "Today · 14:30" or "Yesterday · 22:15"
// or "2026-05-02 · 09:00" for older dates.
const formatVitalsStamp = (date, time) => {
  if (!date && !time) return null;
  if (!date) return time || null;

  const today = new Date();
  const isoToday = today.toISOString().slice(0, 10);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isoYesterday = yesterday.toISOString().slice(0, 10);

  let label = date;
  if (date === isoToday)         label = 'Today';
  else if (date === isoYesterday) label = 'Yesterday';

  return time ? `${label} · ${time}` : label;
};

// Colour the timestamp by recency
const stampColor = (date) => {
  if (!date) return '#94a3b8';
  const today = new Date().toISOString().slice(0, 10);
  return date === today ? '#15803D' : '#94a3b8';   // green if today, grey otherwise
};

const PRIMARY      = '#1A9B7F';
const PRIMARY_LIGHT = `${PRIMARY}14`;

const STATUS_TINTS = {
  Admission:  { bg: '#DCFCE7', text: '#15803D', label: 'Admission'  },
  inPatient:  { bg: '#DCFCE7', text: '#15803D', label: 'Admission'  },
  Detention:  { bg: '#FEF3C7', text: '#A16207', label: 'Detention'  },
  // `Discharge` here means the DOCTOR has discharged. The row remains
  // visible until the ward nurse releases the bed and finalises. Label
  // and tooltip make that expectation obvious — doctors kept seeing the
  // row afterwards and thought discharge had failed.
  Discharge:  {
    bg: '#FEF3C7', text: '#92400E', label: 'Awaiting Nurse',
    hint: "Doctor's discharge is complete. Waiting for the ward nurse to release the bed and finalise.",
  },
  Emergency:  { bg: '#FEE2E2', text: '#991B1B', label: 'Emergency'  },
  // Any unknown / empty value is treated as a regular admission
  default:    { bg: '#DCFCE7', text: '#15803D', label: 'Admission'  },
};

// Resolve the canonical status for a patient row.
// `p.status` is the authoritative field for Detention/Discharge — `p.state`
// is mostly 'inPatient' for active patients regardless of detention.
// Order: status → state → default. Treat 'inPatient' / empty as 'Admission'.
const resolveStatus = (p) => {
  const raw = p.status || p.state;
  if (!raw || raw === 'inPatient') return 'Admission';
  return raw;
};

const initialsOf = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

// Number of whole days a patient has stayed at the ward.
// Active patients: today − visitDate.
// Discharged patients: currtime (discharge time) − visitDate.
const daysAtWard = (p) => {
  if (!p.visitDate) return null;
  const start = new Date(p.visitDate);
  if (Number.isNaN(start.getTime())) return null;
  const isDischarged = (p.status || p.state) === 'Discharge';
  const end = (isDischarged && p.currtime)
    ? new Date(p.currtime)
    : new Date();
  if (Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

// Compact "Xh Ym" elapsed since currtime, for Emergency rows.
const erElapsedLabel = (p) => {
  if (!p.currtime) return null;
  const start = new Date(p.currtime);
  if (Number.isNaN(start.getTime())) return null;
  const diffMin = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
};

// Colour coding by length of stay
const stayColor = (d) => {
  if (d == null) return '#94a3b8';
  if (d <= 0)  return '#16a34a';   // green — admitted today
  if (d <= 3)  return '#475569';   // neutral
  if (d <= 7)  return '#d97706';   // amber — moderate stay
  return '#dc2626';                // red — long stay
};

/**
 * Master patient table for InPatient / Detention / OutPatient panels.
 *
 * Props:
 *   - patients        (array)    list of patients (already filtered upstream)
 *   - onSelectPatient (function) called with the patient object when a row is clicked
 *   - currentUser     (string)   optional, for greeting at top
 *   - variant         (string)   'inpatient' (default) | 'outpatient'.
 *                                Outpatient variant hides ward/admission/discharge concepts:
 *                                  - no admission/detention/discharge filter chips
 *                                  - no Status column on each row
 *                                  - "Visit Date" date header (no days-at-ward subtitle)
 *                                  - subtitle copy refers to "visits today" rather than "assigned to ward"
 *   - dateLabel       (string)   override the date column header
 *   - showStayDays    (boolean)  override whether "X days at ward" is shown under the date
 */
export default function PatientTable({
  patients = [],
  onSelectPatient,
  currentUser = '',
  variant = 'inpatient',
  dateLabel,
  showStayDays,
  // Optional row action — when set, a "Transfer to Ward" column appears
  // before the chevron arrow. Only Emergency-status rows get the active
  // button; the cell stays blank for non-Emergency rows. Used by the ER
  // InPatient view; safe to leave undefined elsewhere.
  onTransferToWard,
  // Server-side pagination mode. When on, filtering/sorting/paging happen
  // in SQL; `patients` is treated as the pre-sliced current page and the
  // controlled state comes from props. Used by the Outpatient Workspace
  // so the browser doesn't hold 13k+ rows at once.
  serverSide = false,
  totalCount,
  page: pageProp,
  rowsPerPage: rowsPerPageProp,
  search: searchProp,
  orderBy: orderByProp,
  order: orderProp,
  onQueryChange,
  loading = false,
  patientCountLabel,
}) {
  const isOutpatient    = variant === 'outpatient';
  const resolvedDateLabel    = dateLabel    !== undefined ? dateLabel    : (isOutpatient ? 'Arrival · Time' : 'Admitted · Days');
  const resolvedShowStayDays = showStayDays !== undefined ? showStayDays : !isOutpatient;
  const showStatus           = !isOutpatient;
  const [searchLocal, setSearchLocal]           = useState('');
  const [statusFilter, setStatusFilter]         = useState('all');
  const [orderByLocal, setOrderByLocal]         = useState('fullName');
  const [orderLocal, setOrderLocal]             = useState('asc');
  const [pageLocal, setPageLocal]               = useState(0);
  const [rowsPerPageLocal, setRowsPerPageLocal] = useState(10);

  // In server mode the parent owns the pagination/search/sort state so it
  // can translate them into API params. In client mode we fall back to the
  // component's own state — no behavior change for existing callers.
  const search      = serverSide ? (searchProp ?? '')          : searchLocal;
  const orderBy     = serverSide ? (orderByProp ?? 'fullName') : orderByLocal;
  const order       = serverSide ? (orderProp ?? 'asc')        : orderLocal;
  const page        = serverSide ? (pageProp ?? 0)             : pageLocal;
  const rowsPerPage = serverSide ? (rowsPerPageProp ?? 25)     : rowsPerPageLocal;

  const emitQuery = (patch) => {
    if (typeof onQueryChange === 'function') {
      onQueryChange({ page, rowsPerPage, search, orderBy, order, ...patch });
    }
  };

  const setSearch = (v) => {
    // Server mode: emit only the search change. Parent debounces and
    // resets page 0 when the debounce settles — emitting page:0 here would
    // trigger a second, immediate refetch from the parent's page effect.
    if (serverSide) emitQuery({ search: v });
    else setSearchLocal(v);
  };
  const setOrderBy = (v) => {
    if (serverSide) emitQuery({ orderBy: v });
    else setOrderByLocal(v);
  };
  const setOrder = (v) => {
    if (serverSide) emitQuery({ order: v });
    else setOrderLocal(v);
  };
  const setPage = (v) => {
    if (serverSide) emitQuery({ page: v });
    else setPageLocal(v);
  };
  const setRowsPerPage = (v) => {
    if (serverSide) emitQuery({ rowsPerPage: v, page: 0 });
    else setRowsPerPageLocal(v);
  };

  // Bill summaries — { [patientId]: { outstanding, pendingToday, pendingCount } }
  const [bills, setBills] = useState({});
  const [billsLoading, setBillsLoading] = useState(false);

  // Stable key derived from patient ids so we only refetch when the list changes
  const patientIdsKey = useMemo(
    () => patients.map(p => p.id).join(','),
    [patients]
  );

  useEffect(() => {
    if (!patientIdsKey) { setBills({}); return; }
    let cancelled = false;
    setBillsLoading(true);

    // Split the patient IDs into chunks so a single request body can't
    // exceed the reverse-proxy body limit (Webuzo/nginx defaults to 1 MB
    // and returns 413 before Node ever sees the request). 100 IDs per
    // batch is well under a few KB and still keeps the total number of
    // round-trips small even for wards with thousands of patients.
    const ids = patientIdsKey.split(',');
    const CHUNK = 100;
    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));

    Promise.all(
      chunks.map(chunk =>
        axios.post(billSummaryBulkdb, { patientIds: chunk })
          .then(res => (res.data && res.data.success ? res.data.results || {} : {}))
          .catch(err => { console.error('billSummaryBulk chunk failed:', err); return {}; })
      )
    )
      .then(chunkResults => {
        if (cancelled) return;
        // Merge every chunk's { [id]: summary } map into one.
        const merged = Object.assign({}, ...chunkResults);
        setBills(merged);
      })
      .finally(() => { if (!cancelled) setBillsLoading(false); });

    return () => { cancelled = true; };
  }, [patientIdsKey]);

  // Stat counts for the chips at the top — keyed by canonical status
  const counts = useMemo(() => {
    const c = { all: patients.length, inPatient: 0, Detention: 0, Discharge: 0, Emergency: 0, critical: 0 };
    patients.forEach(p => {
      const s = resolveStatus(p);
      if (s === 'Detention')      c.Detention++;
      else if (s === 'Discharge') c.Discharge++;
      else if (s === 'Emergency') c.Emergency++;
      else                        c.inPatient++;
      if (p.PrecautionType && p.PrecautionType.trim()) c.critical++;
    });
    return c;
  }, [patients]);

  // Apply search + status filter, then sort. In server mode the parent has
  // already done all three via SQL, so we render `patients` as-is.
  const visibleRows = useMemo(() => {
    if (serverSide) return patients;
    const q = search.trim().toLowerCase();
    const filtered = patients.filter(p => {
      const s = resolveStatus(p);
      if (statusFilter !== 'all') {
        // 'inPatient' bucket only covers regular admissions — Emergency,
        // Detention, and Discharge each have their own chip.
        if (statusFilter === 'inPatient') {
          if (s === 'Detention' || s === 'Discharge' || s === 'Emergency') return false;
        } else if (s !== statusFilter) {
          return false;
        }
      }
      if (!q) return true;
      return (
        (p.fullName || '').toLowerCase().includes(q)
        || String(p.id || '').toLowerCase().includes(q)
        || String(p.tel || '').includes(q)
      );
    });

    const cmp = (a, b) => {
      // Sort by canonical status when ordering by 'status'
      const av = orderBy === 'status' ? resolveStatus(a) : a[orderBy];
      const bv = orderBy === 'status' ? resolveStatus(b) : b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    filtered.sort((a, b) => order === 'asc' ? cmp(a, b) : -cmp(a, b));
    return filtered;
  }, [serverSide, patients, search, statusFilter, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  // In server mode the current page is already what the server returned.
  const pagedRows = serverSide
    ? visibleRows
    : visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  if (!serverSide && page > 0 && pagedRows.length === 0 && visibleRows.length > 0) {
    setPage(0);
  }

  // Row count displayed in the pager and subtitle. Server mode uses the
  // authoritative total from the API; client mode counts the filtered list.
  const paginationCount = serverSide ? (totalCount ?? patients.length) : visibleRows.length;

  // Header columns — Status column is omitted in outpatient mode
  const showTransfer = typeof onTransferToWard === 'function';

  const cols = [
    { id: 'fullName',  label: 'Patient',         sortable: true,  width: showStatus ? '16%' : '18%' },
    { id: 'age',       label: 'Age / Gender',    sortable: true,  width: '9%' },
    ...(showStatus ? [{ id: 'status', label: 'Status', sortable: true, width: '9%' }] : []),
    { id: 'insurance', label: 'Insurance',       sortable: true,  width: '9%' },
    { id: 'bill',      label: 'Bill (GHS)',      sortable: false, width: '11%' },
    { id: 'vitals',    label: 'Last Vitals',     sortable: false, width: '13%' },
    { id: 'visitDate', label: resolvedDateLabel, sortable: true,  width: '11%' },
    { id: 'physicians',label: 'Physicians',      sortable: false, width: '12%' },
    ...(showTransfer ? [{ id: 'transfer', label: 'Action', sortable: false, width: '9%' }] : []),
    { id: 'actions',   label: '',                sortable: false, width: '5%',  align: 'right' },
  ];

  return (
    <Box sx={{ fontFamily: '"Inter","Segoe UI",Arial,sans-serif' }}>

      {/* ── Greeting + counts header ───────────────────────────── */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{
          fontSize: '0.75rem', fontWeight: 700,
          color: PRIMARY, letterSpacing: '1.4px',
          textTransform: 'uppercase', mb: 0.5,
        }}>
          {isOutpatient ? 'Outpatient Workspace' : 'Ward Workspace'}
        </Typography>
        <Typography sx={{
          fontSize: { xs: '1.4rem', md: '1.65rem' },
          fontWeight: 700, color: '#0f172a',
          letterSpacing: '-0.4px', lineHeight: 1.2,
          mb: 0.5,
        }}>
          {currentUser ? `Hello, ${currentUser.split(' ')[0]}` : 'Patient Worklist'}
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.92rem' }}>
          {(() => {
            if (patientCountLabel) return patientCountLabel;
            // Use total from server in server mode; otherwise the loaded list.
            const n = serverSide ? (totalCount ?? patients.length) : patients.length;
            if (n === 0) {
              return isOutpatient
                ? 'No outpatient visits to display.'
                : 'No patients are currently assigned to your ward(s).';
            }
            const word = n === 1 ? 'patient' : 'patients';
            return isOutpatient
              ? `${n} outpatient ${word} to attend to. Click any row to open the chart.`
              : `${n} ${word} assigned to your ward${n === 1 ? '' : 's'}. Click any row to open their chart.`;
          })()}
        </Typography>
      </Box>

      {/* ── Filter chips + search ──────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: 1.5, mb: 2,
        border: '1px solid #e5e9ef', borderRadius: 2.5, bgcolor: '#fff',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          {showStatus && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
            {[
              { key: 'all',       label: 'All',        count: counts.all,       color: '#475569' },
              { key: 'inPatient', label: 'Admission',  count: counts.inPatient, color: '#15803D' },
              { key: 'Emergency', label: 'Emergency',  count: counts.Emergency, color: '#991B1B' },
              { key: 'Detention', label: 'Detention',  count: counts.Detention, color: '#A16207' },
              { key: 'Discharge', label: 'Awaiting Nurse', count: counts.Discharge, color: '#92400E' },
            ].map(f => {
              const active = statusFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  label={`${f.label} (${f.count})`}
                  onClick={() => setStatusFilter(f.key)}
                  size="small"
                  sx={{
                    height: 30,
                    fontWeight: 600, fontSize: '0.78rem',
                    bgcolor: active ? f.color : '#fff',
                    color:   active ? '#fff' : '#334155',
                    border: '1px solid',
                    borderColor: active ? f.color : '#e5e9ef',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: active ? f.color : `${f.color}0D`, borderColor: f.color },
                  }}
                />
              );
            })}
          </Stack>
          )}

          <TextField
            size="small"
            placeholder="Search by name, ID or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              width: { xs: '100%', md: 320 },
              '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} sx={{ color: '#94a3b8' }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {/* ── Table ───────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        border: '1px solid #e2e8f0', borderRadius: 3,
        overflow: 'hidden', bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 24px -12px rgba(15, 23, 42, 0.08)',
      }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow sx={{
                '& th': {
                  bgcolor: '#f1f5f9',
                  borderBottom: '1px solid #e2e8f0',
                  position: 'sticky', top: 0, zIndex: 1,
                },
              }}>
                {cols.map(c => (
                  <TableCell
                    key={c.id}
                    align={c.align || 'left'}
                    sx={{
                      width: c.width,
                      fontWeight: 700, fontSize: '0.72rem',
                      color: '#334155', textTransform: 'uppercase', letterSpacing: '0.6px',
                      py: 1.4,
                    }}
                  >
                    {c.sortable ? (
                      <TableSortLabel
                        active={orderBy === c.id}
                        direction={orderBy === c.id ? order : 'asc'}
                        onClick={() => handleSort(c.id)}
                        sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16 } }}
                      >
                        {c.label}
                      </TableSortLabel>
                    ) : c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={cols.length} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                    <PersonIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                      {search || statusFilter !== 'all'
                        ? 'No patients match your filters.'
                        : 'No patients to show.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : pagedRows.map((p, idx) => {
                const isChild = (p.age != null) && Number(p.age) <= 12;
                const tint = STATUS_TINTS[resolveStatus(p)] || STATUS_TINTS.default;
                const v = p.vitals;
                // Rows the doctor has already discharged should visually
                // recede so they stop looking like active work. Nurses can
                // still click them to finalise; the amber "Awaiting Nurse"
                // chip is what signals it's their next action.
                const isAwaitingNurse = resolveStatus(p) === 'Discharge';
                return (
                  <TableRow
                    key={p.id}
                    hover
                    onClick={() => onSelectPatient && onSelectPatient(p)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isAwaitingNurse
                        ? '#FFFBEB'
                        : (idx % 2 === 1 ? '#fafbfc' : '#fff'),
                      opacity: isAwaitingNurse ? 0.85 : 1,
                      transition: 'background-color .12s ease, opacity .12s ease',
                      '& td': { borderBottom: '1px solid #eef2f7', py: 1.35 },
                      '&:hover': { bgcolor: isAwaitingNurse ? '#FEF3C7' : PRIMARY_LIGHT, opacity: 1 },
                      '&:hover .row-arrow': { opacity: 1, transform: 'translateX(2px)' },
                      '&:last-child td': { borderBottom: 'none' },
                    }}
                  >
                    {/* Patient (avatar + name + ID + phone) */}
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{
                          width: 36, height: 36, fontSize: 13, fontWeight: 700,
                          bgcolor: isChild ? '#FCE7F3' : PRIMARY_LIGHT,
                          color:   isChild ? '#BE185D' : PRIMARY,
                        }}>
                          {isChild ? <ChildCare sx={{ fontSize: 18 }} /> : initialsOf(p.fullName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <Typography sx={{
                              fontWeight: 600, fontSize: '0.88rem', color: '#0f172a',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {p.fullName}
                            </Typography>
                            {p.PrecautionType && p.PrecautionType.trim() && (
                              <Tooltip title={`Precaution: ${p.PrecautionType}`} arrow>
                                <WarningAmber sx={{ fontSize: 16, color: '#dc2626', flexShrink: 0 }} />
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                            ID: {p.id}{p.tel ? ` · ${p.tel}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Age / gender */}
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                        {formatAge(p.dob, p.age)}
                      </Typography>
                      <Chip
                        label={p.gender || '—'}
                        size="small"
                        sx={{
                          mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: p.gender === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                          color:   p.gender === 'MALE' ? '#1D4ED8' : '#BE185D',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </TableCell>

                    {/* Status chip — hidden in outpatient mode. Wrapped in a
                        Tooltip when the tint carries a `hint` (currently only
                        the "Awaiting Nurse" state), so hovering explains why
                        a discharged row is still in the list. */}
                    {showStatus && (
                      <TableCell>
                        {tint.hint ? (
                          <Tooltip title={tint.hint} arrow>
                            <Chip
                              label={tint.label}
                              size="small"
                              sx={{
                                height: 22, fontSize: '0.7rem', fontWeight: 700,
                                bgcolor: tint.bg, color: tint.text,
                                cursor: 'help',
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            label={tint.label}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: tint.bg, color: tint.text,
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        )}
                      </TableCell>
                    )}

                    {/* Insurance */}
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                        {p.insurance || '—'}
                      </Typography>
                    </TableCell>

                    {/* Bill — outstanding balance + today's pending */}
                    <TableCell>
                      {(() => {
                        const b = bills[p.id];
                        if (!b) {
                          return billsLoading
                            ? <Skeleton variant="text" width={80} sx={{ fontSize: '0.85rem' }} />
                            : <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>—</Typography>;
                        }
                        const owing = Number(b.outstanding) || 0;
                        const today = Number(b.pendingToday) || 0;
                        const cnt = Number(b.pendingCount) || 0;
                        const owingColor = balanceColor(owing);
                        return (
                          <Box>
                            <Tooltip title={owing > 0 ? 'Outstanding balance (cumulative)' : 'No outstanding balance'} arrow>
                              <Typography sx={{
                                fontSize: '0.88rem', fontWeight: 700, color: owingColor,
                                lineHeight: 1.2, whiteSpace: 'nowrap',
                              }}>
                                {fmtGHS(owing)}
                              </Typography>
                            </Tooltip>
                            {today > 0 ? (
                              <Tooltip title={`${cnt} pending bill${cnt === 1 ? '' : 's'} created today`} arrow>
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                  mt: 0.25,
                                }}>
                                  <ReceiptIcon sx={{ fontSize: 11, color: '#d97706' }} />
                                  <Typography sx={{
                                    fontSize: '0.7rem', fontWeight: 600, color: '#d97706',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    +{fmtGHS(today)} today
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.25 }}>
                                No bills today
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* Last vitals snapshot + timestamp */}
                    <TableCell>
                      {v ? (
                        <Box>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {v.temperature != null && (
                              <Chip size="small" label={`T ${v.temperature}°`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#FEF2F2', color: '#B91C1C', '& .MuiChip-label': { px: 0.65 } }} />
                            )}
                            {v.bp && (
                              <Chip size="small" label={`BP ${v.bp}`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#EFF6FF', color: '#1D4ED8', '& .MuiChip-label': { px: 0.65 } }} />
                            )}
                            {v.pulse != null && (
                              <Chip size="small" label={`P ${v.pulse}`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#FDF4FF', color: '#A21CAF', '& .MuiChip-label': { px: 0.65 } }} />
                            )}
                            {v.spo2 != null && (
                              <Chip size="small" label={`SpO₂ ${v.spo2}`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#ECFDF5', color: '#047857', '& .MuiChip-label': { px: 0.65 } }} />
                            )}
                          </Stack>
                          {(() => {
                            const stamp = formatVitalsStamp(v.recorded_date, v.recorded_time);
                            if (!stamp) return null;
                            return (
                              <Tooltip title={`Recorded ${v.recorded_date || ''} ${v.recorded_time || ''}`.trim()} arrow>
                                <Typography sx={{
                                  fontSize: '0.68rem', fontWeight: 600,
                                  color: stampColor(v.recorded_date),
                                  mt: 0.4, whiteSpace: 'nowrap',
                                }}>
                                  {stamp}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          No recent vitals
                        </Typography>
                      )}
                    </TableCell>

                    {/* Visit date (+ arrival time for outpatients / days at ward for inpatients) */}
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>
                        {p.visitDate || '—'}
                      </Typography>
                      {isOutpatient && p.visitTime && (
                        <Typography sx={{
                          fontSize: '0.72rem', fontWeight: 700,
                          color: PRIMARY, mt: 0.25,
                          letterSpacing: '0.3px',
                        }}>
                          {p.visitTime}
                        </Typography>
                      )}
                      {resolvedShowStayDays && (() => {
                        const isEmergency = resolveStatus(p) === 'Emergency';
                        if (isEmergency) {
                          const elapsed = erElapsedLabel(p);
                          if (!elapsed) return null;
                          return (
                            <Typography sx={{
                              fontSize: '0.7rem', fontWeight: 700,
                              color: '#dc2626', mt: 0.25,
                            }}>
                              ⏱ {elapsed}
                            </Typography>
                          );
                        }
                        const d = daysAtWard(p);
                        if (d == null) return null;
                        const color = stayColor(d);
                        const isDischarged = resolveStatus(p) === 'Discharge';
                        const label = d === 0 ? 'Today' : `${d} day${d === 1 ? '' : 's'}`;
                        return (
                          <Typography sx={{
                            fontSize: '0.7rem', fontWeight: 700,
                            color, mt: 0.25,
                          }}>
                            {label}{isDischarged ? ' (stay)' : ''}
                          </Typography>
                        );
                      })()}
                    </TableCell>

                    {/* Physicians who touched this visit (labs / scans / meds / diagnosis) */}
                    <TableCell>
                      {p.physicians ? (
                        <Tooltip title={p.physicians} arrow>
                          <Typography sx={{
                            fontSize: '0.75rem', color: '#334155',
                            fontWeight: 500, lineHeight: 1.35,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {p.physicians}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Transfer-to-Ward action — only when host view supplies the handler */}
                    {showTransfer && (
                      <TableCell>
                        {resolveStatus(p) === 'Emergency' ? (
                          <Tooltip title="Transfer this patient to the regular Wards queue. The bed is released; days-at-facility counter is preserved." arrow>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SwapHoriz sx={{ fontSize: 14 }} />}
                              onClick={(e) => { e.stopPropagation(); onTransferToWard(p); }}
                              sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '0.7rem',
                                py: 0.25, px: 1, minHeight: 26,
                                borderColor: '#1A9B7F', color: '#1A9B7F',
                                '&:hover': { borderColor: '#157763', bgcolor: 'rgba(26,155,127,0.08)' },
                              }}
                            >
                              Transfer
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</Typography>
                        )}
                      </TableCell>
                    )}

                    {/* Action arrow */}
                    <TableCell align="right">
                      <Tooltip title="Open chart" arrow>
                        <IconButton
                          size="small"
                          className="row-arrow"
                          onClick={(e) => { e.stopPropagation(); onSelectPatient && onSelectPatient(p); }}
                          sx={{
                            color: PRIMARY,
                            opacity: 0.55,
                            transition: 'opacity .15s ease, transform .15s ease',
                            '&:hover': { bgcolor: PRIMARY_LIGHT, opacity: 1 },
                          }}
                        >
                          <ArrowForwardIos sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {(serverSide || visibleRows.length > rowsPerPage) && (
          <TablePagination
            component="div"
            count={paginationCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: '1px solid #e5e9ef' }}
          />
        )}
      </Paper>
    </Box>
  );
}
